// ✅ src/pages/VehicleV2/BatterySupplierDetailReport_Print.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "https://nissi-india.com/api/batterySupplierDetailsReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};
const pad2 = (n) => String(n).padStart(2, "0");

const fmtDDMMYYYY = (iso) => {
  if (!iso) return "";
  const s = toStr(iso);
  if (s.includes("/")) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};
const toMoney = (v) =>
  toNum(v, 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

export default function BatterySupplierDetailReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ received from list page
  const passed = location?.state || {};

  const branchType = passed.branchType || "BRANCH";
  const branchId = Number(passed.branchId) || 0;
  const loginUserId = Number(passed.loginUserId) || 0;

  const filters = passed.filters || { fromDate: "", toDate: "", supplier: "Select", serialNo: "" };
  const currentPage = Number(passed.currentPage) || 1;
  const itemsPerPage = Number(passed.itemsPerPage) || 10;
  const totalRecordsFromList = Number(passed.totalRecords) || 0;
  const printScope = passed.printScope || "ALL"; // ALL | CURRENT_PAGE

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      // ✅ if user opened print page directly without state
      if (!branchId) throw new Error("Print params missing. Open print from report page.");

      const params = {
        branchType,
        branchId,
        loginUserId,
        fromDate: filters.fromDate || "",
        toDate: filters.toDate || "",
        supplier: isSelect(filters.supplier) ? "" : filters.supplier,
        serialNo: toStr(filters.serialNo),
      };

      const { data } = await axios.get(`${API_BASE}/list`, { params });
      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      setRows(data?.rows || []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // mapping (same fields as backend list)
  const getSupplierName = (r) => pick(r, ["fld_SupplierName", "fld_suppliername", "SupplierName", "supplierName"], "-");
  const getSerialNo = (r) => pick(r, ["fld_battery_serialno", "BatterySerialNo", "serialNo"], "-");
  const getPurchaseDate = (r) => fmtDDMMYYYY(pick(r, ["fld_purchaseddate", "PurchaseDate", "purchasedDate"], ""));
  const getRate = (r) => pick(r, ["fld_purchaseamt", "Rate", "rate"], "0.00");

  const totalRecords = rows.length;
  const totalPages = Math.ceil(totalRecords / itemsPerPage) || 1;

  // ✅ Decide what to print
  const printRows = useMemo(() => {
    if (printScope === "CURRENT_PAGE") {
      const start = (currentPage - 1) * itemsPerPage;
      return rows.slice(start, start + itemsPerPage);
    }
    return rows;
  }, [rows, printScope, currentPage, itemsPerPage]);

  const grandTotal = useMemo(() => {
    return printRows.reduce((sum, r) => sum + toNum(getRate(r), 0), 0);
  }, [printRows]);

  return (
   
      <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
        {/* HeaderBar only for screen */}
        \

        <div className="w-full bg-white">
          {/* ✅ PRINT CSS */}
          <style>{`
            @page { size: A4 portrait; margin: 10mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          {/* Toolbar */}
          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading
                ? "Loading..."
                : errMsg
                ? <span className="text-red-600">{errMsg}</span>
                : `Scope: ${printScope} | Page: ${currentPage}/${totalPages} | Records: ${totalRecords} (list had ${totalRecordsFromList})`}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>
              <button
                onClick={() => window.print()}
                disabled={loading || !printRows.length}
                className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>
            </div>
          </div>

          {/* ✅ PRINT TEMPLATE */}
          <div className="print-area mx-auto max-w-[820px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                {/* Header (like screenshot) */}
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>

                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="border-b border-black text-center font-bold py-2 text-sm">
                  Battery Card Supplier Detail Report
                </div>

                {/* Filter info + page info */}
                <div className="border-b border-black p-2 text-[12px]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div><span className="font-semibold">From Date</span> : {filters.fromDate ? fmtDDMMYYYY(filters.fromDate) : "-"}</div>
                      <div><span className="font-semibold">To Date</span> : {filters.toDate ? fmtDDMMYYYY(filters.toDate) : "-"}</div>
                      <div><span className="font-semibold">Serial No</span> : {filters.serialNo || "-"}</div>
                    </div>
                    <div className="text-right">
                      <div><span className="font-semibold">Scope</span> : {printScope}</div>
                      <div><span className="font-semibold">Page</span> : {printScope === "CURRENT_PAGE" ? `${currentPage} / ${totalPages}` : `All (pages: ${totalPages})`}</div>
                      <div><span className="font-semibold">Records</span> : {printRows.length}</div>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="p-2">
                  <table className="w-full border border-black border-collapse text-[12px]">
                    <thead>
                      <tr>
                        <th className="border border-black px-2 py-1 w-[8%]">S.No</th>
                        <th className="border border-black px-2 py-1 w-[32%]">Supplier Name</th>
                        <th className="border border-black px-2 py-1 w-[24%]">Battery Serial No</th>
                        <th className="border border-black px-2 py-1 w-[18%]">Purchase Date</th>
                        <th className="border border-black px-2 py-1 w-[18%] text-right">Rate</th>
                      </tr>
                    </thead>

                    <tbody>
                      {printRows.map((r, idx) => (
                        <tr key={pick(r, ["fld_batteryid", "batteryId", "id"], `${idx}`)}>
                          <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>
                          <td className="border border-black px-2 py-1">{getSupplierName(r)}</td>
                          <td className="border border-black px-2 py-1">{getSerialNo(r)}</td>
                          <td className="border border-black px-2 py-1 text-center">{getPurchaseDate(r) || "-"}</td>
                          <td className="border border-black px-2 py-1 text-right">{toMoney(getRate(r))}</td>
                        </tr>
                      ))}

                      <tr>
                        <td className="border border-black px-2 py-1 font-bold text-right" colSpan={4}>
                          Total
                        </td>
                        <td className="border border-black px-2 py-1 font-bold text-right">{toMoney(grandTotal)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>

            {/* Debug */}
            {rows?.length > 0 && (
              <div className="no-print mt-4 text-xs text-gray-600">
                <details>
                  <summary className="cursor-pointer">Debug: View state + fetched rows</summary>
                  <pre className="mt-2 p-3 border rounded bg-gray-50 overflow-auto">
                    {JSON.stringify({ passed, fetchedRowsCount: rows.length }, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
   
  );
}
